import { useState } from "react";
import { useSnackbar } from "notistack";
import { useFunctionLogs } from "../../hooks/useFunctionLogs";
import { usePersonalization } from "../../hooks/useMutations";
import { errorPayload, statusFromMutation } from "../../lib/mutationStatus";
import { CampaignInput } from "../CampaignInput";
import { JsonResult } from "../results/JsonResult";
import { PersonalizationResult } from "../results/PersonalizationResult";
import { LogStream } from "../LogStream";
import { StatusLine } from "../StatusLine";

export function PersonalizationCard() {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = usePersonalization();
  const [runStartTime, setRunStartTime] = useState<Date | null>(null);
  const logs = useFunctionLogs(
    "cf_personalized_line",
    runStartTime,
    mutation.isPending,
  );

  const [campaignId, setCampaignId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [additionalEmailContext, setAdditionalEmailContext] = useState("");

  const onRun = () => {
    if (!campaignId.startsWith("cam_")) {
      enqueueSnackbar("Campaign ID must start with cam_.", {
        variant: "warning",
      });
      return;
    }
    logs.reset();
    setRunStartTime(new Date());
    mutation.mutate({
      campaign_id: campaignId,
      dry_run: dryRun,
      additional_email_context: additionalEmailContext.trim() || undefined,
    });
  };

  const { variant, message } = statusFromMutation(mutation);

  return (
    <section className="card">
      <h2>Personalization</h2>
      <p className="card-desc">
        Fetches leads from a Lemlist campaign, crawls their company websites,
        generates a personalized intro line with AI, and injects it back into
        Lemlist as the <code>{"{{personalizedLine}}"}</code> variable.
      </p>
      <div className="field">
        <label htmlFor="p-campaign">Lemlist Campaign ID</label>
        <CampaignInput
          id="p-campaign"
          value={campaignId}
          onChange={setCampaignId}
        />
      </div>
      <div className="field">
        <label htmlFor="p-email-context">
          Campaign email body override (optional)
        </label>
        <div
          style={{
            color: "var(--text-muted, #888)",
            marginBottom: 4,
            fontSize: "0.85em",
          }}
        >
          By default the email body is fetched from the Lemlist campaign itself
          (the step that contains <code>{"{{personalizedLine}}"}</code>) and
          used as context for the AI. Use this field only if you want to
          override that for this run. If both Lemlist and this field are empty,
          the prompt falls back to:
          <pre
            style={{
              marginTop: 4,
              padding: "6px 8px",
              background: "var(--surface-muted, #f5f5f5)",
              border: "1px solid var(--border, #e0e0e0)",
              borderRadius: 4,
              fontSize: "0.85em",
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
            }}
          >
            {`Dear Herr Müller,

{{personalizedLine}}

That's exactly the problem OriginVault solves...

Worth a 20-minute call?`}
          </pre>
        </div>
        <textarea
          id="p-email-context"
          value={additionalEmailContext}
          onChange={(e) => setAdditionalEmailContext(e.target.value)}
          placeholder={"Paste your campaign email here..."}
          rows={2}
          style={{ width: "100%", fontFamily: "inherit", resize: "vertical" }}
        />
      </div>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        Dry run
      </label>
      <button onClick={onRun} disabled={mutation.isPending}>
        Run
      </button>
      <StatusLine variant={variant} message={message} />
      <LogStream
        entries={logs.entries}
        isPolling={logs.isPolling}
        lastError={logs.lastError}
      />
      {mutation.isSuccess && <PersonalizationResult data={mutation.data} />}
      {mutation.isError && <JsonResult data={errorPayload(mutation.error)} />}
    </section>
  );
}
